import React, { useLayoutEffect, useRef, useState } from 'react';
import Button from './Button';
import ArrowLeftIcon from './CustomIcons/ArrowLeftIcon';
import { GLASS_SURFACE_STYLE } from './glassSurface';

export interface WizardLayoutProps {
    /** Renders the outline back button in the top-left slot. */
    showBack?: boolean;
    onBack?: () => void;
    backDisabled?: boolean;
    /** Top-left slot beside the back button — e.g. a user avatar menu. */
    leading?: React.ReactNode;
    /** Right top-bar slot (sits just before `stepper`) — e.g. a theme toggle. */
    stepper?: React.ReactNode;
    /** Far-right top-bar slot — e.g. a theme toggle. */
    trailing?: React.ReactNode;
    /**
     * Pinned below the top bar and always visible while the content scrolls —
     * e.g. connection logos + a step title. Rendered in the same centred column
     * as the content (`contentWidth`).
     */
    header?: React.ReactNode;
    /** Pinned to the bottom of the screen — typically a <FooterNav /> or a primary CTA. */
    footer?: React.ReactNode;
    /**
     * Give the pinned footer a frosted-glass treatment (translucent background +
     * backdrop blur) so scrolling content shows through it. The footer already sits
     * above sticky body content, so it reads as glass over the scrolling list.
     */
    footerGlass?: boolean;
    /** Vertically centre the content (used for terminal/success screens). */
    center?: boolean;
    /** Max width of the centered content column. */
    contentWidth?: number;
    /** Minimum width of the whole layout, below which it scrolls horizontally. */
    minWidth?: number;
    children?: React.ReactNode;
    className?: string;
}

const WizardLayout: React.FC<WizardLayoutProps> = ({
    showBack = false,
    onBack,
    backDisabled = false,
    leading,
    stepper,
    trailing,
    header,
    footer,
    footerGlass = false,
    center = false,
    contentWidth = 480,
    minWidth,
    children,
    className = '',
}) => {
    // The footer overlays the bottom of the scroll area (so content can glass under
    // it). Measure it so the scroll content reserves matching bottom space and the
    // last item is never hidden behind it.
    const footerRef = useRef<HTMLDivElement>(null);
    const [footerHeight, setFooterHeight] = useState(0);

    // biome-ignore lint/correctness/useExhaustiveDependencies: re-run when the footer node mounts/unmounts so the observer attaches to the current element.
    useLayoutEffect(() => {
        const el = footerRef.current;
        if (!el) {
            setFooterHeight(0);
            return;
        }
        const update = () => setFooterHeight(el.offsetHeight);
        update();
        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => observer.disconnect();
    }, [footer]);

    return (
        <div
            className={`w-full h-screen flex flex-col overflow-hidden bg-[var(--malachite-card-sunken-background)] ${className}`}
            style={minWidth ? { minWidth: `${minWidth}px` } : undefined}
        >
            <div className="w-full shrink-0 flex items-center justify-between px-6 pt-4 min-h-[var(--malachite-control-height-small,28px)]">
                <div className="flex items-center gap-[16px] min-w-0">
                    {showBack ? (
                        <Button
                            variant="outline"
                            size="small"
                            iconOnly
                            icon={<ArrowLeftIcon size={14} />}
                            disabled={backDisabled}
                            onClick={onBack}
                            aria-label="Back"
                            className="w-[var(--malachite-control-height-small,28px)] h-[var(--malachite-control-height-small,28px)] shrink-0"
                        />
                    ) : (
                        <span className="w-[var(--malachite-control-height-small,28px)] h-[var(--malachite-control-height-small,28px)] shrink-0" />
                    )}
                    {leading}
                </div>
                <div className="flex items-center gap-[16px]">
                    {trailing ?? null}
                    {stepper ?? null}
                </div>
            </div>

            {header ? (
                <div className="w-full shrink-0 flex flex-col items-center px-6 pt-8">
                    <div
                        className="w-full flex flex-col items-center gap-[24px]"
                        style={{ maxWidth: `${contentWidth}px` }}
                    >
                        {header}
                    </div>
                </div>
            ) : null}

            <div className="relative flex-1 min-h-0">
                <div
                    className={`absolute inset-0 overflow-y-auto flex flex-col items-center px-6 ${
                        center ? 'justify-center' : 'justify-start'
                    }`}
                    style={{
                        // Reserve the overlaid footer's height plus a gap, so the last item
                        // clears the footer with breathing room (not flush against it, which
                        // reads as hidden content continuing underneath).
                        paddingBottom: footer ? `${footerHeight + 24}px` : '48px',
                    }}
                >
                    <div
                        className="w-full flex flex-col items-center gap-[40px]"
                        style={{
                            maxWidth: `${contentWidth}px`,
                            // Top padding lives on the scrolling content — NOT the scrollport —
                            // so a `position: sticky` child pins flush to the top with no gap.
                            paddingTop: header ? '32px' : '48px',
                        }}
                    >
                        {children}
                    </div>
                </div>

                {footer ? (
                    <div
                        ref={footerRef}
                        // z-20 keeps the pinned footer above sticky body content (e.g. a
                        // sticky Accordion header at z-10) so it can't paint over the footer.
                        className="absolute inset-x-0 bottom-0 z-20"
                        style={footerGlass ? GLASS_SURFACE_STYLE : undefined}
                    >
                        {footer}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default WizardLayout;
export { WizardLayout };
